import { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { validateResetToken, resetPassword } from '../services/auth';
import EyeToggle from '../components/EyeToggle';
import { RE_UPPERCASE, RE_LOWERCASE, RE_NUMBER, RE_SPECIAL, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';

const inputClass = 'mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

const ResetPasswordPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false, hasUpperCase: false, hasLowerCase: false,
    hasNumber: false, hasSpecialChar: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    return () => { if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current); };
  }, []);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setIsValidating(false);
        return;
      }
      try {
        await validateResetToken(token);
        setTokenValid(true);
      } catch (err) {
        setError('This reset link is invalid or has expired. Please request a new password reset.');
      } finally {
        setIsValidating(false);
      }
    };
    checkToken();
  }, [token]);

  useEffect(() => {
    const v = {
      minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
      hasUpperCase: RE_UPPERCASE.test(newPassword),
      hasLowerCase: RE_LOWERCASE.test(newPassword),
      hasNumber: RE_NUMBER.test(newPassword),
      hasSpecialChar: RE_SPECIAL.test(newPassword),
    };
    setPasswordValidation(v);
    setPasswordStrength(Object.values(v).filter(Boolean).length);
  }, [newPassword]);

  useEffect(() => {
    setPasswordsMatch(confirmPassword.length > 0 && newPassword === confirmPassword);
  }, [newPassword, confirmPassword]);

  const getStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-gray-300';
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength === 3) return 'bg-yellow-500';
    if (passwordStrength === 4) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength === 3) return 'Medium';
    if (passwordStrength === 4) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < PASSWORD_MIN_LENGTH) { setError('Password must be at least 8 characters long'); return; }
    if (!RE_UPPERCASE.test(newPassword) || !RE_LOWERCASE.test(newPassword) || !RE_NUMBER.test(newPassword) || !RE_SPECIAL.test(newPassword)) {
      setError('Password must contain uppercase, lowercase, number, and special character (@$!%*?&-)');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(token, newPassword, confirmPassword);
      setSuccess(true);
      redirectTimeoutRef.current = setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const RightPanel = ({ children, heading, subtext }) => (
    <div className={`flex-1 flex flex-col items-center justify-start px-6 py-10 bg-white dark:bg-gray-900 overflow-y-auto ${darkMode ? 'dark' : ''}`}>
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="text-center mb-8">
          <span className="inline-block text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 px-4 py-1.5 rounded-lg font-bold uppercase tracking-wide mb-3">Beta</span>
          <Link to="/">
            <p className="text-sm md:text-base tracking-widest uppercase text-blue-600 dark:text-blue-400 font-semibold">Stonewall Invoicing</p>
          </Link>
        </div>
        <p className="text-center mb-4">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors hover:-translate-x-0.5 transform duration-150">← Back to sign in</Link>
        </p>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-1 text-center">{heading}</h2>
        {subtext && <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">{subtext}</p>}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-8">
          {children}
        </div>
      </div>
    </div>
  );

  if (isValidating) {
    return (
      <RightPanel heading="Reset your password" subtext="Validating your reset link">
        <div className="flex flex-col items-center py-6 gap-4">
          <svg className="animate-spin h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">Validating reset link...</p>
        </div>
      </RightPanel>
    );
  }

  if (!tokenValid) {
    return (
      <RightPanel heading="Invalid link" subtext="This reset link is no longer valid">
        <div className="flex flex-col items-center gap-6 py-2">
          <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center">{error}</p>
          <Link to="/forgot-password" className="w-full text-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
            Request new reset link
          </Link>
          <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
            ← Back to sign in
          </Link>
        </div>
      </RightPanel>
    );
  }

  if (success) {
    return (
      <RightPanel heading="Password reset" subtext="You're all set">
        <div className="flex flex-col items-center gap-4 py-4">
          <svg className="h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
            Your password has been reset. Redirecting to sign in...
          </p>
        </div>
      </RightPanel>
    );
  }

  const CheckRow = ({ met, label }) => (
    <div className={`flex items-center text-xs gap-2 ${met ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        {met
          ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          : <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        }
      </svg>
      {label}
    </div>
  );

  return (
    <RightPanel heading="Create new password" subtext="Enter your new password below">
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 mb-6">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
          <div className="relative">
            <input
              id="new-password" name="new-password" type={showPassword ? 'text' : 'password'}
              autoComplete="new-password" required
              className={inputClass} placeholder="••••••••"
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={isLoading}
            />
            <EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>

          {newPassword.length > 0 && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 mr-3">
                  <div className={`h-1.5 rounded-full transition-all duration-300 ${getStrengthColor()}`} style={{ width: `${(passwordStrength / 5) * 100}%` }} />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 w-12 text-right">{getStrengthLabel()}</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <CheckRow met={passwordValidation.minLength} label="8+ characters" />
                <CheckRow met={passwordValidation.hasUpperCase} label="Uppercase" />
                <CheckRow met={passwordValidation.hasLowerCase} label="Lowercase" />
                <CheckRow met={passwordValidation.hasNumber} label="Number" />
                <CheckRow met={passwordValidation.hasSpecialChar} label="Special char (@$!%*?&-)" />
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
          <div className="relative">
            <input
              id="confirm-password" name="confirm-password" type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password" required
              className={inputClass} placeholder="••••••••"
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={isLoading}
            />
            <EyeToggle show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
          </div>
          {confirmPassword.length > 0 && (
            <p className={`mt-1 text-xs ${passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {passwordsMatch ? '✓ Passwords match' : 'Passwords do not match'}
            </p>
          )}
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={isLoading || passwordStrength < 5 || !passwordsMatch}
            className="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading && (
              <svg className="animate-spin mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isLoading ? 'Resetting...' : 'Reset password'}
          </button>
        </div>
      </form>

    </RightPanel>
  );
};

export default ResetPasswordPage;
